import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';
import { useCutscene, cutsceneBridge } from '../city/cutscene';
import { roomSpace } from '../state/interiorFloor';
import { useSceneChat, isNarrow, columnPx } from './SceneChat';

// THE CAMERA OF A CHAT SCENE (components/SceneChat). One, in the canvas
// (PlanetScene), copying RoomGreeting's hold: while a chat that names a face is
// open, the camera is theirs — the walk, the clamps, the follow camera, the seat
// and the doors stand down (cutsceneBridge), the HUD steps out (the cutscene
// phase with no target: no letterbox, no cue), and the chat lays itself over the
// shot.
//
// WHERE THE FACE IS — two ways to say it (SceneChat):
//   { roomId, face, rotY }  a fixed point in room space (the front desk)
//   { anchor, eye }         the group their body stands in, followed live
//                           (2026-11-01, every artist in the world): the head
//                           bone where the body has one (standing or sitting,
//                           any height), else `eye` up the anchor; their facing
//                           is the anchor's +z. They turn to you as the chat
//                           opens (a held resident faces the camera; a room
//                           person's goal does), so the camera comes in from
//                           your side — open ground, wherever they were facing.
//
// THE SHOT: first person, in front of them at their eye line, up close, so they
// face the lens. Wide windows put them left of centre — in the middle of what the
// chat column leaves; narrow ones centre them with the face high, above the chat.
// Re-framed every frame, so a resized window keeps the layout. When the chat
// closes the camera eases back to exactly where you stood, looking where you
// looked. A greeting already playing is asked to finish first.
const IN_S = 1.2;
const OUT_S = 0.9;
// Once the shot holds, the camera does not walk with them (2026-11-01, Khari: "if
// you initiate a conversation with an avatar while they are walking, the chat
// scene will follow them"). A person who moves off their spot — a clerk fetching
// what you asked for — is followed by TURNING, from where the camera stands, like
// an operator would; back within this much of it, the shot re-frames as before.
const STAY_R = 0.25;
// …AND IT DOES NOT SWAY WITH THEM (2026-11-01, Khari: "the camera sways as the
// artist sways and pivots … It makes the screen hard to look at while reading").
// The head bone moves with every breath, gesture and turn; aimed at it each
// frame, the whole picture rocked. So the face is taken from where they stand
// plus a head height measured ONCE (the bone, the first time it is there), and
// once the shot has settled the camera is LOCKED: it re-frames only for a new
// window size, and turns only if they walk off their spot (above).
const NEAR = 0.5;        // wide: this far in front of the face (room units, ≈1.1 m): up close
const NEAR_TALL = 0.62;  // narrow: a little further, so the shoulders fit a tall frame
const FACE_HIGH = 0.4;   // narrow: the face this far above centre (of the half-height)
const FACE_LIFT = 0.16;  // wide: and a touch above centre
const ease = (x) => { const t = Math.min(1, Math.max(0, x)); return t * t * (3 - 2 * t); };

/** Where the camera looks, as offsets from the face in the frame: { across, down } in world units at distance d. */
export function framing(w, h, fovDeg, d) {
  const t = Math.tan(THREE.MathUtils.degToRad(fovDeg / 2));
  const aspect = w / Math.max(1, h);
  if (isNarrow(w, h)) return { across: 0, down: FACE_HIGH * d * t };
  const free = w - columnPx(w);                 // what the column leaves, from the left edge
  const ndc = (free / 2 / w) * 2 - 1;           // its middle, in -1..1 across the frame
  return { across: -ndc * d * t * aspect, down: FACE_LIFT * d * t };
}

/**
 * The shot, in world space: `face` (their eye line) and `dir` (the way they face,
 * level, unit length), `unit` = one room unit in world units. Writes the camera
 * position into `pos` and the point it looks at into `look`.
 */
export function shotPose(face, dir, unit, w, h, fovDeg, pos, look, right = new THREE.Vector3(), up = UP) {
  const d = (isNarrow(w, h) ? NEAR_TALL : NEAR) * unit;
  pos.copy(face).addScaledVector(dir, d);
  // The camera looks along -dir; its right is up × dir.
  right.crossVectors(up, dir).normalize();
  const f = framing(w, h, fovDeg, d);
  look.copy(face).addScaledVector(right, f.across).addScaledVector(up, -f.down);
}
const UP = new THREE.Vector3(0, 1, 0);

/** The head bone of a body under `anchor` (Meshy: "Head"; Mixamo: "mixamorig:Head"), or null. */
function headOf(anchor) {
  let head = null;
  anchor.traverse((o) => { if (!head && o.isBone && /(^|[:_|])head$/i.test(o.name || '')) head = o; });
  return head;
}

export default function ChatSceneCamera() {
  const camera = useThree((s) => s.camera);
  // A desk's scene belongs to its room: walked out, it is over.
  const scene = useWorldStore((s) => {
    const sc = s.chatAvatar?.scene || s.shop?.scene || null;   // an artist's chat, or a counter's (ShopDock)
    if (!sc) return null;
    if (sc.roomId && sc.roomId !== s.interior) return null;
    return sc;
  });
  const sceneRef = useRef(scene);
  sceneRef.current = scene;
  const run = useRef(null);
  const v = useMemo(() => ({
    face: new THREE.Vector3(), tip: new THREE.Vector3(), dir: new THREE.Vector3(), right: new THREE.Vector3(),
    up: new THREE.Vector3(0, 1, 0), pos: new THREE.Vector3(), look: new THREE.Vector3(),
    m: new THREE.Matrix4(), q: new THREE.Quaternion(),
  }), []);

  const finish = (restore) => {
    const r = run.current;
    run.current = null;
    if (!r) return;
    if (restore) { camera.position.copy(r.savedPos); camera.quaternion.copy(r.savedQuat); }
    if (cutsceneBridge.scene?.kind === 'chat') { cutsceneBridge.active = false; cutsceneBridge.at = null; cutsceneBridge.scene = null; }
    const cs = useCutscene.getState();
    if (cs.phase === 'cut' && !cs.target) useCutscene.setState({ phase: 'off' });
    useSceneChat.setState({ on: false });
  };
  // Left the room with the chat open: let go; the rig owns the camera again.
  useEffect(() => () => finish(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  /** This frame's shot into v.pos / v.q; false when the room (or the body) is not there. */
  const shot = (s, w, h) => {
    let unit;
    if (s.anchor) {
      const a = s.anchor;
      // Gone from the scene (they walked off, the room unloaded): over.
      let root = a; while (root.parent) root = root.parent;
      if (!root.isScene) return false;
      a.updateWorldMatrix(true, false);
      v.face.set(0, 0, 0); a.localToWorld(v.face);            // their feet
      v.tip.set(0, 0, 1); a.localToWorld(v.tip);
      v.dir.subVectors(v.tip, v.face);
      unit = v.dir.length() || 1;                               // one of their units, in world units
      // Their eye height above where they stand, measured once off the head bone
      // (standing or sitting, any height; +0.04: the bone sits at the base of the
      // skull) — never re-read, so a breath or a nod cannot move the shot.
      if (s._eyeY == null) {
        const head = headOf(a);
        if (head) { head.getWorldPosition(v.tip); s._eyeY = v.tip.y - v.face.y + 0.04 * unit; }
      }
      v.face.y += s._eyeY ?? (s.eye || 0.68) * unit;
    } else {
      const space = roomSpace.object;
      if (!space || !Array.isArray(s.face)) return false;
      const [fx, fy, fz] = s.face;
      const rot = s.rotY || 0;
      v.face.set(fx, fy, fz); space.localToWorld(v.face);
      v.tip.set(fx + Math.sin(rot), fy, fz + Math.cos(rot)); space.localToWorld(v.tip);
      v.dir.subVectors(v.tip, v.face);
      unit = v.dir.length() || 1;   // one room unit, in world units (the room's own scale)
    }
    v.dir.y = 0; v.dir.normalize();
    v.unit = unit;
    shotPose(v.face, v.dir, unit, w, h, camera.fov || 55, v.pos, v.look, v.right, v.up);
    v.m.lookAt(v.pos, v.look, v.up);
    v.q.setFromRotationMatrix(v.m);
    return true;
  };

  useFrame((state) => {
    const s = sceneRef.current;
    const { width: w, height: h } = state.size;
    let r = run.current;
    if (s && !r) {
      // Someone else has the camera: a greeting is asked to finish; anything else is waited out.
      if (cutsceneBridge.active) {
        if (cutsceneBridge.scene?.kind === 'greet' && useCutscene.getState().target?.kind === 'greet') useCutscene.getState().leave();
        return;
      }
      if (!shot(s, w, h)) return;
      const savedPos = camera.position.clone();
      const savedQuat = camera.quaternion.clone();
      cutsceneBridge.active = true; cutsceneBridge.at = savedPos.clone(); cutsceneBridge.scene = { kind: 'chat', id: s.avatarId || null };
      useCutscene.setState({ phase: 'cut', target: null, caption: null });
      useSceneChat.setState({ on: true });
      r = run.current = { stage: 'in', t0: performance.now(), savedPos, savedQuat, fromPos: savedPos.clone(), fromQuat: savedQuat.clone() };
    }
    if (!r) return;
    // Closed: back to where you stood.
    if (!s && r.stage !== 'out') {
      r.stage = 'out'; r.t0 = performance.now();
      r.fromPos = camera.position.clone(); r.fromQuat = camera.quaternion.clone();
      useSceneChat.setState({ on: false });
    }
    const t = (performance.now() - r.t0) / 1000;   // the wall clock: a slow device must not stretch it
    if (r.stage === 'out') {
      const e = ease(t / OUT_S);
      camera.position.lerpVectors(r.fromPos, r.savedPos, e);
      camera.quaternion.slerpQuaternions(r.fromQuat, r.savedQuat, e);
      if (e >= 1) finish(true);
      return;
    }
    if (!shot(s, w, h)) { finish(false); return; }
    if (r.stage === 'in') {
      const e = ease(t / IN_S);
      camera.position.lerpVectors(r.fromPos, v.pos, e);
      camera.quaternion.slerpQuaternions(r.fromQuat, v.q, e);
      if (e >= 1) { r.stage = 'hold'; r.spot = v.face.clone(); r.lock = { pos: v.pos.clone(), q: v.q.clone(), w, h }; }   // where they stand; the shot, locked
      return;
    }
    if (r.spot && v.face.distanceTo(r.spot) > STAY_R * (v.unit || 1)) {
      // Moved off their spot: the camera stays where it stood when they left it,
      // and turns to keep them in the frame.
      if (!r.panFrom) r.panFrom = camera.position.clone();
      camera.position.copy(r.panFrom);
      v.m.lookAt(r.panFrom, v.look, v.up);
      camera.quaternion.setFromRotationMatrix(v.m);
      return;
    }
    r.panFrom = null;
    // On their spot: the locked shot, still — re-framed only for a new window size.
    if (!r.lock || r.lock.w !== w || r.lock.h !== h) r.lock = { pos: v.pos.clone(), q: v.q.clone(), w, h };
    camera.position.copy(r.lock.pos);
    camera.quaternion.copy(r.lock.q);
  });

  return null;
}
